// LightLogicGrids.jsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo, faLightbulb, faCheckCircle, faQuestionCircle, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function LightLogicGrids({ gridSize = 6, youtubeLink }) {
  const [showHelp, setShowHelp] = useState(false);
  const [notification, setNotification] = useState(null);
  const [grid, setGrid] = useState([]);

  // Generate empty grid
  const generateGrid = () => {
    const newGrid = Array(gridSize).fill(null).map(() =>
      Array(gridSize).fill(null).map(() => null)
    );
    setGrid(newGrid);
  };

  useEffect(() => {
    generateGrid();
  }, []);

  const toggleCell = (r, c) => {
    const newGrid = grid.map(row => [...row]);
    newGrid[r][c] = newGrid[r][c] === 1 ? 0 : 1; // simple toggle
    setGrid(newGrid);
  };

  const checkSolution = () => {
    // placeholder: mark all cells as correct for demo
    setNotification({ type: 'success', message: '✅ Solution checked!' });
    setTimeout(() => setNotification(null), 2000);
  };

  const undo = () => {
    generateGrid();
    setNotification({ type: 'success', message: '🔄 Grid reset!' });
    setTimeout(() => setNotification(null), 2000);
  };

  const giveHint = () => {
    // placeholder: toggle first empty cell
    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (grid[r][c] === null) {
          const newGrid = grid.map(row => [...row]);
          newGrid[r][c] = 1;
          setGrid(newGrid);
          setNotification({ type: 'success', message: '💡 Hint applied!' });
          setTimeout(() => setNotification(null), 2000);
          return;
        }
      }
    }
  };

  return (
    <div className="p-2 min-h-screen bg-black text-gray-300 flex flex-col items-center">
      
      {/* Top Buttons */}
      <div className="w-full flex justify-center gap-4 items-center mb-4">
        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 flex justify-center items-center gap-2"
          onClick={() => window.history.back()}
        >
          ← Back
        </button>

        <button
          className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-blue-600 text-black rounded-lg hover:bg-blue-500 flex justify-center items-center gap-2"
          onClick={() => setShowHelp(true)}
        >
          <FontAwesomeIcon icon={faQuestionCircle} /> Help
        </button>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 p-4">
          <div className="bg-gray-900 p-6 rounded-xl max-w-lg w-full text-gray-300 flex flex-col gap-4">
            <h3 className="text-m lg:text-xl font-bold text-green-400">📖 How to Play Light Logic Grids</h3>
            <p className='text-m lg:text-lg'>
              Fill the 6×6 grid logically according to simple puzzle rules. Toggle cells to solve the puzzle.
            </p>
            <ul className="list-disc list-inside text-gray-400">
              <li>Tap a cell to toggle between empty/filled.</li>
              <li>Use Hint button to auto-fill a correct cell.</li>
              <li>Undo or Reset to start over.</li>
              <li>Press Check when you think the puzzle is complete.</li>
            </ul>
            {youtubeLink && (
              <a
                href={youtubeLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-red-600 text-black rounded-lg hover:bg-red-500 text-center font-semibold"
              >
                ▶ Watch
              </a>
            )}
            <button
              className="w-20 text-xs lg:text-m lg:w-40 px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 mt-2"
              onClick={() => setShowHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg text-sm font-semibold ${
            notification.type === 'error' ? 'bg-red-700 text-white' : 'bg-green-600 text-black'
          } flex justify-between items-center shadow-lg z-50 w-80`}
        >
          <span>{notification.message}</span>
          <button
            className="w-10 p-1 text-lg flex items-center justify-center"
            onClick={() => setNotification(null)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Title */}
      <h2 className="text-lg lg:text-2xl font-bold mb-2 text-green-400">🧩 Light 6×6 Logic Grids</h2>
      <p className="text-gray-400 mb-4 text-center max-w-md text-xs lg:text-m">
        Small logic grids for quick mental clarity and stress relief.
      </p>

      {/* Grid */}
      <div className="flex flex-col items-center mb-4">
        {grid.map((row, rIdx) => (
          <div key={rIdx} className="flex">
            {row.map((cell, cIdx) => (
              <div
                key={cIdx}
                onClick={() => toggleCell(rIdx, cIdx)}
                className={`w-12 h-12 border border-gray-600 m-0.5 flex items-center justify-center text-gray-300 font-bold cursor-pointer hover:bg-gray-800 transition ${
                  cell === 1 ? 'bg-green-400' : cell === 0 ? 'bg-gray-700' : 'bg-gray-900'
                }`}
              >
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="mt-2 flex flex-row gap-4 flex-wrap justify-center">
        <button
          onClick={checkSolution}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-green-600 text-black font-semibold rounded-lg hover:bg-green-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faCheckCircle} /> Check
        </button>
        <button
          onClick={undo}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-gray-700 text-gray-300 font-semibold rounded-lg hover:bg-gray-600 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faUndo} /> Undo
        </button>
        <button
          onClick={giveHint}
          className="w-20 text-xs lg:text-m lg:w-40 px-2 py-2 bg-blue-600 text-black font-semibold rounded-lg hover:bg-blue-500 transition flex justify-center items-center gap-2"
        >
          <FontAwesomeIcon icon={faLightbulb} /> Hint
        </button>
      </div>
    </div>
  );
}
